import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BrandDetails } from "../types/add-brand-types";
import { useLeadsApi } from "../api/use-leads-api";
import { LeadApiItem, LeadTableItem } from "../types/leads";
import { trackEvent } from "../utils/firebase-analytics";

const DEFAULT_PAGE = 1;
const LEADS_PAGE_SIZE = 15;

const COMPANY_PREFIX_REGEX = /^Company\s*:\s*/i;

const normalizeTargetType = (value: string | null | undefined): string => {
  if (value === "community_signal") return "Community signal";
  if (value === "role") return "Role target";
  return "Company";
};

const parseCompanyName = (notes: string | null): string => {
  if (!notes) return "--";
  const lines = notes
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const companyLine = lines.find((line) => COMPANY_PREFIX_REGEX.test(line));
  if (!companyLine) return "--";

  return companyLine.replace(COMPANY_PREFIX_REGEX, "").trim() || "--";
};

const parseCompanyType = (notes: string | null): string => {
  if (!notes) return "--";

  const lines = notes
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const nonCompanyLine = lines.find((line) => !COMPANY_PREFIX_REGEX.test(line));
  return nonCompanyLine || "--";
};

const mapLeadToTableItem = (lead: LeadApiItem): LeadTableItem => {
  const metadata = lead.metadata || {};
  const notesCompanyName = parseCompanyName(lead.notes);
  const companyName =
    metadata.company ||
    (notesCompanyName !== "--" ? notesCompanyName : null) ||
    lead.name ||
    "--";
  const fitReason = metadata.fit_reason || parseCompanyType(lead.notes);
  const sourceUrl = metadata.source_url || null;
  const linkedinSearchQuery =
    metadata.linkedin_search_query ||
    [companyName, metadata.target_type || "", "LinkedIn"].filter(Boolean).join(" ");

  return {
    id: lead.id,
    companyName,
    targetType: normalizeTargetType(metadata.target_type),
    signal: metadata.pain_signal || metadata.evidence_snippet || "--",
    fitReason: fitReason || "--",
    sourceUrl,
    sourceTitle: metadata.source_title || (sourceUrl ? "Source" : "--"),
    outreachAngle: metadata.outreach_angle || fitReason || "--",
    suggestedDm: metadata.suggested_dm || "",
    linkedinSearchQuery,
    confidenceScore:
      typeof metadata.confidence_score === "number"
        ? metadata.confidence_score
        : null,
    email: lead.email || "--",
    phone: lead.phone || "--",
  };
};

export const useLeads = (brandDetails: BrandDetails) => {
  const { getLeads, generateLeads } = useLeadsApi();
  const prevBrandIdRef = useRef<string>("");

  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  const [leadItems, setLeadItems] = useState<LeadTableItem[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);

  const fetchLeads = useCallback(
    async (pageNum: number = DEFAULT_PAGE) => {
      if (!brandDetails.id) {
        setLeadItems([]);
        setTotalLeads(0);
        setTotalPages(1);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const response = await getLeads(
        brandDetails.id,
        pageNum,
        LEADS_PAGE_SIZE
      );

      if (response) {
        setLeadItems(response.data.map(mapLeadToTableItem));
        setTotalLeads(response.total);
        setTotalPages(Math.max(1, response.total_pages || 1));
        setPage(response.page || pageNum);
      } else {
        setLeadItems([]);
        setTotalLeads(0);
        setTotalPages(1);
      }

      setIsLoading(false);
    },
    [brandDetails.id, getLeads]
  );

  useEffect(() => {
    if (!brandDetails.id) return;

    if (prevBrandIdRef.current !== brandDetails.id) {
      prevBrandIdRef.current = brandDetails.id;
      fetchLeads(DEFAULT_PAGE);
      return;
    }

    fetchLeads(page);
  }, [brandDetails.id, page, fetchLeads]);

  const refreshLeads = useCallback(() => {
    fetchLeads(DEFAULT_PAGE);
  }, [fetchLeads]);

  const generateProspects = useCallback(async () => {
    if (!brandDetails.id || isGenerating) return null;
    setIsGenerating(true);
    trackEvent("lead_generation_started");
    const response = await generateLeads(brandDetails.id);
    await fetchLeads(DEFAULT_PAGE);
    setIsGenerating(false);
    if (response) {
      trackEvent("lead_generation_completed", {
        saved_count: response.data.saved,
        generated_count: response.data.generated ?? 0,
        duplicate_count: response.data.duplicates,
      });
    } else {
      trackEvent("lead_generation_failed");
    }
    return response;
  }, [brandDetails.id, fetchLeads, generateLeads, isGenerating]);

  const goToPrevPage = useCallback(() => {
    setPage((prev) => Math.max(1, prev - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPage((prev) => Math.min(totalPages, prev + 1));
  }, [totalPages]);

  const hasLeads = useMemo(() => totalLeads > 0, [totalLeads]);

  return {
    isLoading,
    page,
    totalPages,
    totalLeads,
    leadItems,
    hasLeads,
    isGenerating,
    refreshLeads,
    generateProspects,
    goToPrevPage,
    goToNextPage,
  };
};
